<template>
  <div>
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <h2 class="subtitle">
      {{ $t("promotionalImage.promotionalImageTitle") }}
      &nbsp;&nbsp;
      <button
        class="button is-small"
        style="position: relative; bottom: 2px;"
        @click="$refs.promotionalImage.click()"
        v-if="
          !storeBeingManaged.promotions.promotionalImage ||
            !storeBeingManaged.promotions.promotionalImage.link
        "
      >
        <b-icon icon="plus-circle" type="is-primary"></b-icon>
      </button>

      <button
        class="button is-small"
        style="position: relative; bottom: 2px;"
        @click="removePromotionalImage"
        v-else
      >
        <b-icon icon="trash" type="is-primary"></b-icon>
        &nbsp;&nbsp;
        {{ $t("common.remove") }}
      </button>
    </h2>

    <input
      type="file"
      ref="promotionalImage"
      style="display: none"
      accept="image/png, image/jpeg, image/jpg"
      @change="selectPromotionalImage"
    />

    <promotional-image-preview-modal
      :active="isPreviewModalOpen"
      :image-url="previewImageUrl"
      :is-uploading="isUploadingPromotionalImage"
      @cancel="cancelPromotionalImagePreview"
      @confirm="confirmPromotionalImageUpload"
    />

    <p
      v-if="
        !storeBeingManaged.promotions.promotionalImage ||
          !storeBeingManaged.promotions.promotionalImage.link
      "
    >
      {{ $t("promotionalImage.addPromotionalImage") }}
      <br />
      {{ $t("promotionalImage.promotionalImageDetails") }}
    </p>

    <div v-else>
      <p>
        →
        <span
          @click="isPromotionalImagePopUpOpen = true"
          style="color: blue; cursor: pointer;"
        >
          <u>{{ $t("promotionalImage.promotionalImageTitle") }}</u>
        </span>
        {{ $t("promotionalImage.toShowWhenClientEntersStore") }}
      </p>
      <br />

      <div class="columns">
        <div class="column">
          <b-field :label="$t('promotionalImage.status')">
            <b-switch
              size="is-small"
              class="promotional-image-switch"
              type="is-primary"
              v-model="storeBeingManaged.promotions.promotionalImage.isActive"
              passive-type="is-secondary"
              @input="togglePromotionalImageStatus"
            ></b-switch>

            {{
              storeBeingManaged.promotions.promotionalImage.isActive
                ? $t("common.Active-f")
                : $t("common.Inactive-f")
            }}
          </b-field>
        </div>

        <div class="column">
          <b-field :label="$t('settings.beginDate')" label-position="on-border">
            <b-input
              :placeholder="$t('settings.select')"
              icon="calendar-day"
              custom-class="is-clickable"
              @click.native="openBeginDateModal"
              @focus="openBeginDateModal"
              id="beginDateId"
              :value="getBeginDateFormattedValue"
            ></b-input>
          </b-field>
        </div>

        <div class="column">
          <b-field :label="$t('settings.endDate')" label-position="on-border">
            <b-input
              :placeholder="$t('settings.select')"
              icon="calendar-day"
              custom-class="is-clickable"
              @click.native="openEndDateModal"
              @focus="openEndDateModal"
              id="endDateId"
              :value="getEndDateFormattedValue"
            ></b-input>
          </b-field>
        </div>
      </div>
    </div>

    <promotional-image-preview-modal
      mode="view"
      :active="isPromotionalImagePopUpOpen"
      :image-url="promotionalImageLink"
      @cancel="isPromotionalImagePopUpOpen = false"
    />

    <b-modal
      :active.sync="isBeginDateModalActive"
      can-cancel
      :on-cancel="saveBeginDateAfterModalClose"
    >
      <b-datetimepicker
        v-model="beginDate"
        inline
        class="has-text-centered"
        locale="pt-BR"
      >
        <template #right>
          <b-button
            :label="$t('common.ok')"
            type="is-primary"
            @click="
              saveBeginDateAfterModalClose(), (isBeginDateModalActive = false)
            "
          />
        </template>
      </b-datetimepicker>
    </b-modal>

    <b-modal
      :active.sync="isEndDateModalActive"
      can-cancel
      :on-cancel="saveEndDateAfterModalClose"
    >
      <b-datetimepicker
        v-model="endDate"
        inline
        class="has-text-centered"
        locale="pt-BR"
      >
        <template #right>
          <b-button
            :label="$t('common.ok')"
            type="is-primary"
            @click="
              saveEndDateAfterModalClose(), (isEndDateModalActive = false)
            "
          />
        </template>
      </b-datetimepicker>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../../mixins/common";
import { mask } from "../../../mixins/mask";
import { compressPromotionalImage } from "../../../utils/promotionalImage";
import PromotionalImagePreviewModal from "./PromotionalImagePreviewModal.vue";

export default {
  mixins: [common, mask],

  components: {
    PromotionalImagePreviewModal
  },

  data() {
    return {
      isLoading: false,
      isPromotionalImagePopUpOpen: false,
      isBeginDateModalActive: false,
      isEndDateModalActive: false,
      isPreviewModalOpen: false,
      isUploadingPromotionalImage: false,
      pendingPromotionalImage: null,
      previewImageUrl: "",
      beginDate: null,
      endDate: null
    };
  },

  computed: {
    ...mapGetters(["storeBeingManaged", "preferences"]),

    promotionalImageLink() {
      const promotionalImage = this.storeBeingManaged.promotions
        .promotionalImage;
      return promotionalImage && promotionalImage.link
        ? promotionalImage.link
        : "";
    },

    getBeginDateFormattedValue() {
      return this.beginDate !== null
        ? this.moment
            .utc(this.beginDate)
            .add(this.preferences.timezone, "h")
            .format("DD/MM/YY, HH:mm")
        : "";
    },

    getEndDateFormattedValue() {
      return this.endDate !== null
        ? this.moment
            .utc(this.endDate)
            .add(this.preferences.timezone, "h")
            .format("DD/MM/YY, HH:mm")
        : "";
    }
  },

  methods: {
    async selectPromotionalImage(event) {
      const inputEl = event && event.target ? event.target : null;
      const file =
        inputEl && inputEl.files && inputEl.files[0] ? inputEl.files[0] : null;

      if (inputEl) {
        inputEl.value = "";
      }

      if (!file) {
        return;
      }

      if (
        file.type !== "image/png" &&
        file.type !== "image/jpg" &&
        file.type !== "image/jpeg"
      ) {
        this.alertError({
          message: "FILE_EXTENSION_NOT_ALLOWED_PNG_JPG_JPEG"
        });
        return;
      }

      this.isLoading = true;

      try {
        const compressedFile = await compressPromotionalImage(file);
        this.discardPendingPromotionalImage();
        this.pendingPromotionalImage = compressedFile;
        this.previewImageUrl = URL.createObjectURL(compressedFile);
        this.isPreviewModalOpen = true;
      } catch (err) {
        this.alertPromotionalImageError(err);
      } finally {
        this.isLoading = false;
      }
    },

    async confirmPromotionalImageUpload() {
      if (!this.pendingPromotionalImage) {
        return;
      }

      this.isUploadingPromotionalImage = true;

      try {
        const formData = new FormData();
        formData.append("file", this.pendingPromotionalImage);
        formData.append("storeId", this.storeBeingManaged._id);

        const res = await this.$store.dispatch("uploadFile", {
          to: "upload-promotional-image",
          data: formData
        });

        if (res.data.result === "error") {
          this.alertError(res.data);
          return;
        }

        this.$buefy.toast.open({
          duration: 3000,
          message: this.$t("promotionalImage.promotionalImageAdded"),
          position: "is-bottom",
          type: "is-primary"
        });

        if (!this.storeBeingManaged.promotions.promotionalImage) {
          this.storeBeingManaged.promotions.promotionalImage = {
            link: res.data.payload.link
          };
        } else {
          this.storeBeingManaged.promotions.promotionalImage.link =
            res.data.payload.link;
        }

        this.$store.dispatch("updatePromotionalImageLink", {
          link: res.data.payload.link,
          storeId: this.storeBeingManaged._id
        });

        this.cancelPromotionalImagePreview();
      } catch (err) {
        this.alertPromotionalImageError(err);
      } finally {
        this.isUploadingPromotionalImage = false;
      }
    },

    cancelPromotionalImagePreview() {
      this.isPreviewModalOpen = false;
      this.discardPendingPromotionalImage();
    },

    discardPendingPromotionalImage() {
      if (this.previewImageUrl) {
        URL.revokeObjectURL(this.previewImageUrl);
      }
      this.previewImageUrl = "";
      this.pendingPromotionalImage = null;
    },

    alertPromotionalImageError(err) {
      console.log("[PromotionalImage] upload error", err);
      const errorMessage = err && err.message ? err.message : "UNEXPECTED_ERROR";

      if (errorMessage === "FILE_EXTENSION_NOT_ALLOWED_PNG_JPG_JPEG") {
        this.alertError({
          message: "FILE_EXTENSION_NOT_ALLOWED_PNG_JPG_JPEG"
        });
      } else if (
        errorMessage === "IMAGE_TOO_LARGE_FOR_LIMIT" ||
        errorMessage === "FILE_IS_TOO_LARGE_200"
      ) {
        this.alertError({ message: "FILE_IS_TOO_LARGE_200" });
      } else {
        this.alertError(err);
      }
    },

    removePromotionalImage() {
      this.isLoading = true;
      this.$store
        .dispatch("removePromotionalImage", {
          storeId: this.storeBeingManaged._id,
          link: ""
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("promotionalImage.promotionalImageDeleted"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.storeBeingManaged.promotions.promotionalImage.link = "";
            this.$store.dispatch("updatePromotionalImageLink", {
              link: "",
              storeId: this.storeBeingManaged._id
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    togglePromotionalImageStatus() {
      this.isLoading = true;
      this.$store
        .dispatch("togglePromotionalImageStatus", {
          storeId: this.storeBeingManaged._id,
          status: this.storeBeingManaged.promotions.promotionalImage.isActive
        })
        .then(res => {
          if (res.data.result === "error") {
            this.storeBeingManaged.promotions.promotionalImage.isActive = !this
              .storeBeingManaged.promotions.promotionalImage.isActive;
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: `${this.$t("promotionalImage.promotionalImageTitle")} ${
                this.storeBeingManaged.promotions.promotionalImage.isActive
                  ? this.$t("common.active-f")
                  : this.$t("common.inactive-f")
              }`,
              position: "is-bottom",
              type: "is-primary"
            });
          }
        })
        .catch(() => {
          this.storeBeingManaged.promotions.promotionalImage.isActive = !this
            .storeBeingManaged.promotions.promotionalImage.isActive;
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    openBeginDateModal() {
      this.isBeginDateModalActive = true;
    },

    openEndDateModal() {
      this.isEndDateModalActive = true;
    },

    saveBeginDateAfterModalClose() {
      console.log(this.beginDate);
      if (this.beginDate !== null) {
        let beginDateEl = document.getElementById("beginDateId");
        beginDateEl.setAttribute("value", this.beginDate);
      }

      this.isLoading = true;
      this.$store
        .dispatch("updatePromotionalImageDate", {
          storeId: this.storeBeingManaged._id,
          date: "beginDate",
          value: this.beginDate
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: `${this.$t("promotionalImage.dateUpdated")}`,
              position: "is-bottom",
              type: "is-primary"
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    saveEndDateAfterModalClose() {
      console.log(this.endDate);
      if (this.endDate !== null) {
        let endDateEl = document.getElementById("endDateId");
        endDateEl.setAttribute("value", this.endDate);
      }

      this.isLoading = true;
      this.$store
        .dispatch("updatePromotionalImageDate", {
          storeId: this.storeBeingManaged._id,
          date: "endDate",
          value: this.endDate
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: `${this.$t("promotionalImage.dateUpdated")}`,
              position: "is-bottom",
              type: "is-primary"
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  mounted() {
    const beginDate = new Date(
      this.storeBeingManaged.promotions.promotionalImage.beginDate
    );
    const endDate = new Date(
      this.storeBeingManaged.promotions.promotionalImage.endDate
    );

    if (
      beginDate instanceof Date &&
      beginDate.getTime() > new Date().getTime() - 1000 * 60 * 60 * 24 * 365
    ) {
      this.beginDate = beginDate;
    } else {
      this.beginDate = new Date();
    }

    if (
      endDate instanceof Date &&
      endDate.getTime() > new Date().getTime() - 1000 * 60 * 60 * 24 * 365
    ) {
      this.endDate = endDate;
    } else {
      this.endDate = new Date();
    }
  },

  beforeDestroy() {
    this.discardPendingPromotionalImage();
  }
};
</script>

<style scoped>
.promotional-image-switch {
  position: relative;
  top: 2px;
  left: 6px;
  -webkit-transform: scaleX(1);
  transform: scaleX(1);
}
</style>
